import { type Dispatch } from 'redux';
import { type TFunction } from 'i18next';

import { Utils } from 'app/main/components';
import { showSendDemToMailupDialog } from '../dems-messages/DemsSendToMailupDialog';
import { showSendDemToMailchimpDialog } from '../dems-messages/DemsSendToMailchimpDialog';
import { showOpenDemsAlertDialog } from './OpenDemsAlertDialog';

export interface DemsManagerActionParams {
	dispatch: Dispatch;
	t: TFunction;
	demsConnector: 'mailup' | 'mailchimp';
	demsUid: string;
	userUid: string;
}

export function setConnector(
	{ dispatch, t, demsConnector, demsUid, userUid }: DemsManagerActionParams,
	onSuccess: VoidFunction
) {
	dispatch(Utils.alertProgressMsg(t));
	Utils.executeStored({
		action: 'st_dems_update',
		data: { dems_uid: demsUid, dems_user_modified_uid: userUid, dems_connector: demsConnector },
		dispatch,
		successHandler: () => {
			dispatch(Utils.alertSuccessMsg(t));
			onSuccess();
		}
	});
}

export function loadDefaultRecipients(
	{ dispatch, t, demsUid, userUid }: DemsManagerActionParams,
	onSuccess: VoidFunction
) {
	Utils.executeStored({
		action: 'st_dems_recipients_add_multiple',
		dispatch,
		data: {
			derc_dems_uid: demsUid,
			derc_user_created_uid: userUid,
			filters: [{ filt_field: 'mail_dems_default', filt_clause: 'equals', filt_value: 1 }]
		},
		successHandler: () => {
			dispatch(Utils.alertSuccessMsg(t));
			onSuccess();
		}
	});
}

export function uploadRecipients(
	{ dispatch, t, demsConnector, demsUid }: DemsManagerActionParams,
	onSuccess: VoidFunction
) {
	dispatch(Utils.alertProgressMsg(t));
	Utils.executeApi.post({
		url: `/api/${demsConnector}/dem/${demsUid}/uploadRecipients`,
		dispatch,
		successHandler: () => {
			dispatch(Utils.alertSuccessMsg(t));
			onSuccess();
		},
		errorHandler: error => {
			if (error.data.type === 'Mailchimp.MaxListNumber') dispatch(showOpenDemsAlertDialog());
			else dispatch(Utils.alertApiError(error));
		}
	});
}

export function cancelSend({ dispatch, t, demsConnector, demsUid }: DemsManagerActionParams, onSuccess: VoidFunction) {
	dispatch(Utils.alertProgressMsg(t));
	Utils.executeApi.delete({
		url: `/api/${demsConnector}/dem/${demsUid}/cancelSend`,
		dispatch,
		successHandler: () => {
			dispatch(Utils.alertSuccessMsg(t));
			onSuccess();
		}
	});
}

export function sendDem({ dispatch, demsConnector, demsUid }: DemsManagerActionParams, onSuccess: VoidFunction) {
	if (demsConnector === 'mailup') dispatch(showSendDemToMailupDialog({ demsUid, onSuccess }));
	if (demsConnector === 'mailchimp') dispatch(showSendDemToMailchimpDialog({ demsUid, onSuccess }));
}

export function unschedule({ dispatch, t, demsConnector, demsUid }: DemsManagerActionParams, onSuccess: VoidFunction) {
	dispatch(Utils.alertProgressMsg(t));
	Utils.executeApi.delete({
		url: `/api/${demsConnector}/dem/${demsUid}/unschedule`,
		dispatch,
		successHandler: () => {
			dispatch(Utils.alertSuccessMsg(t));
			onSuccess();
		}
	});
}

export function checkSendStatus(
	{ dispatch, t, demsConnector, demsUid, userUid }: DemsManagerActionParams,
	onSuccess: VoidFunction
) {
	dispatch(Utils.alertProgressMsg(t));
	const connection = Utils.getConnectionFromToken();
	Utils.executeApi.post({
		url: `/webhook/${demsConnector}/dem/${demsUid}/setSent?conn=${connection}&userUid=${userUid}`,
		// url: `/api/${demsConnector}/dem/${demsUid}/checkSent`,
		dispatch,
		successHandler: () => {
			dispatch(Utils.alertSuccessMsg(t));
			onSuccess();
		}
	});
}

export function retrieveStats(
	{ dispatch, t, demsConnector, demsUid }: DemsManagerActionParams,
	onSuccess: VoidFunction
) {
	dispatch(Utils.alertProgressMsg(t));
	Utils.executeApi.get({
		url: `/api/${demsConnector}/dem/${demsUid}/stats`,
		dispatch,
		successHandler: () => {
			dispatch(Utils.alertSuccessMsg(t));
			onSuccess();
		}
	});
}

export function deleteMailchimpList(
	{ dispatch, t, demsUid }: Pick<DemsManagerActionParams, 'dispatch' | 't' | 'demsUid'>,
	onSuccess: VoidFunction
) {
	dispatch(Utils.alertProgressMsg(t));
	Utils.executeApi.get({
		url: `/api/mailchimp/dem/${demsUid}/stats`,
		dispatch,
		successHandler: response => {
			Utils.executeApi.delete({
				url: `/api/mailchimp/dem/${demsUid}/delete`,
				dispatch,
				successHandler: () => {
					dispatch(Utils.alertSuccessMsg(t));
					onSuccess();
				}
			});
		}
	});
}

export default [
	setConnector,
	loadDefaultRecipients,
	uploadRecipients,
	cancelSend,
	sendDem,
	unschedule,
	retrieveStats,
	deleteMailchimpList
];
